import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { usePlayer } from "./PlayerProvider";
import IconButton from "../components/IconButton";
import { trackEvent } from "../../analytics";
import { fmt } from "./MiniPlayer";
import { RotateBackGlyph, RotateForwardGlyph } from "./transportGlyphs";
import { PlayIcon, PauseIcon, ChevronDownIcon, DownloadIcon, ArrowForwardIcon } from "../components/icons";
import { LikeButton, BookmarkButton } from "../components/EngagementButtons";
import { Scrubber, VolumeControl, MoreMenu } from "./controls";

/*
 * Expanded player — bottom sheet, in the class-page card's "docked bar"
 * language (player redesign 2026-10): backdrop, drag handle + collapse
 * chevron, 16:9 video flush to the sheet edges (the SAME persistent video
 * element, reparented in) or square artwork, title + context, full-width
 * scrubber with times beneath, ↺10 / 72px play / ↻10 (the sheet's one focal
 * control), then one borderless row — like · bookmark · speed · volume · ⋯
 * (class page, download) — and "Up next in this series". Backdrop, chevron
 * or Escape collapses.
 */

export default function ExpandedSheet() {
  const player = usePlayer();
  const navigate = useNavigate();
  const current = useSelector((s) => s.player.current);
  const playing = useSelector((s) => s.player.playing);
  const position = useSelector((s) => s.player.position);
  const duration = useSelector((s) => s.player.duration);
  const speed = useSelector((s) => s.player.speed);
  const muted = useSelector((s) => s.player.muted);
  const volume = useSelector((s) => s.player.volume);
  const expanded = useSelector((s) => s.player.expanded);
  const queue = useSelector((s) => s.player.queue);

  const videoMountRef = useRef(null);
  const isVideo = current?.mediaKind === "video";

  // Claim the persistent video element while open. Priority 3: the sheet
  // outranks every other surface; releasing hands the node back to the
  // class-page card or mini-player thumb instead of parking it.
  useEffect(() => {
    if (expanded && isVideo && videoMountRef.current) {
      return player.claimVideo(videoMountRef.current, 3);
    }
    return undefined;
  }, [expanded, isVideo, player]);

  // Escape closes; lock body scroll while open.
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") player.setExpanded(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [expanded, player]);

  if (!current || !expanded) return null;

  const dur = duration || current.durationHint || 0;
  const next = queue[0];
  const downloadHref = current.src || current.streamUrl;

  const menuItems = [
    current.href && {
      key: "class",
      icon: <ArrowForwardIcon size={18} />,
      label: "Go to class page",
      onSelect: () => {
        player.setExpanded(false);
        navigate(current.href);
      },
    },
    downloadHref && {
      key: "download",
      icon: <DownloadIcon size={18} />,
      label: isVideo ? "Download video" : "Download audio",
      href: downloadHref,
      onSelect: () => {
        try {
          trackEvent({ category: "player", action: "download", label: current.title });
        } catch (_) {}
      },
    },
  ].filter(Boolean);

  return (
    <div className="ic-scope fixed inset-x-0 bottom-0 z-[70] flex flex-col justify-end font-sans">
      <button
        type="button"
        aria-label="Close expanded player"
        onClick={() => player.setExpanded(false)}
        className="fixed inset-0 bg-black/50 border-0 cursor-pointer focus-visible:outline-none"
      />
      <div
        role="dialog"
        aria-label="Now playing"
        className="ic-sheet relative bg-ic-navy rounded-t-ic-hero shadow-ic-modal max-w-[640px] w-full mx-auto px-6 pt-3 pb-8 flex flex-col gap-4 box-border overflow-y-auto"
        style={{ maxHeight: "85vh" }}
      >
        {/* Drag handle row */}
        <div className="flex items-center justify-between">
          <span className="w-11" />
          <span className="w-12 h-[5px] rounded-[3px] bg-white/30" />
          <IconButton variant="bareDark" size={44} dark label="Collapse player" onClick={() => player.setExpanded(false)}>
            <ChevronDownIcon size={20} />
          </IconButton>
        </div>

        {/* Media surface */}
        {isVideo ? (
          <div
            ref={videoMountRef}
            className="relative flex-shrink-0 -mx-6 bg-[#0D1420] overflow-hidden"
            style={{ aspectRatio: "16/9", minHeight: 180 }}
            onClick={player.toggle}
          />
        ) : (
          <div className="flex justify-center">
            <div
              className="rounded-2xl bg-ic-navyCard flex flex-col items-center justify-center gap-1 shadow-ic-floating"
              style={{ width: "min(200px, 44vw)", aspectRatio: "1/1" }}
            >
              <span dir="rtl" className="text-[42px] font-extrabold text-ic-sky leading-[52px]">שיעור</span>
              <span className="text-[10px] font-bold tracking-[0.5px] text-ic-lightGray uppercase">
                {current.seriesTitle || "Inside Chassidus"}
              </span>
            </div>
          </div>
        )}

        {/* Title + context */}
        <div className="flex flex-col gap-1 text-center">
          <span className="text-[20px] font-bold leading-7 text-white ic-wrap-anywhere">{current.title}</span>
          {(current.seriesTitle || current.positionLabel) && (
            <span className="text-[14px] leading-5 text-ic-lightGray">
              {[current.seriesTitle, current.positionLabel].filter(Boolean).join(" · ")}
            </span>
          )}
        </div>

        {/* Scrubber strip, times beneath */}
        <div className="flex flex-col">
          <Scrubber value={position} max={dur || 1} onSeek={player.seekTo} trackHeight={4} thumb={14} />
          <div className="flex justify-between text-[13px] leading-4 text-ic-lightGray tabular-nums">
            <span>{fmt(position)}</span>
            <span>{dur ? fmt(dur) : "—"}</span>
          </div>
        </div>

        {/* Transport */}
        <div className="flex items-center justify-center gap-4">
          <IconButton variant="bareDark" size={48} dark label="Back 10 seconds" onClick={() => player.seekBy(-10)}>
            <RotateBackGlyph size={26} />
          </IconButton>
          <IconButton variant="solid" dark size={72} label={playing ? "Pause" : "Play"} onClick={player.toggle}>
            {playing ? <PauseIcon size={28} /> : <PlayIcon size={28} />}
          </IconButton>
          <IconButton variant="bareDark" size={48} dark label="Forward 10 seconds" onClick={() => player.seekBy(10)}>
            <RotateForwardGlyph size={26} />
          </IconButton>
        </div>

        {/* Secondary row — same borderless controls as the class-page bar */}
        <div className="flex items-center justify-center gap-1">
          {current.classId && (
            <>
              <LikeButton target="class" id={current.classId} surface="navyBare" />
              <BookmarkButton target="class" id={current.classId} surface="navyBare" />
            </>
          )}
          <button
            type="button"
            aria-label={`Playback speed ${speed}×`}
            onClick={player.cycleSpeed}
            className="min-w-[44px] h-11 px-1.5 rounded-full bg-transparent border-0 text-ic-lightGray font-sans text-[14px] font-bold tabular-nums cursor-pointer transition-all duration-150 ease-out hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
          >
            {speed}×
          </button>
          <VolumeControl muted={muted} volume={volume} onMute={() => player.setMuted(!muted)} onVolume={(v) => player.setVolume(v)} />
          {menuItems.length > 0 && <MoreMenu items={menuItems} />}
        </div>

        {/* Up next */}
        {next && (
          <div className="border-t border-white/10 pt-3 flex flex-col gap-2">
            <span className="text-[12px] font-bold tracking-[0.5px] uppercase text-ic-lightGray">Up next in this series</span>
            <div className="flex items-center gap-3">
              <span className="flex-shrink-0 w-9 h-9 rounded-full bg-white/10 inline-flex items-center justify-center text-ic-lightGray text-[13px] font-bold">
                {next.indexLabel || "▶"}
              </span>
              <div className="flex-1 min-w-0 flex flex-col">
                <span className="text-[16px] font-semibold leading-6 text-ic-offwhite ic-wrap-anywhere">{next.title}</span>
                {next.metaLabel && <span className="text-[13px] leading-[18px] text-ic-lightGray">{next.metaLabel}</span>}
              </div>
              <button
                type="button"
                aria-label="Play next class"
                onClick={() => {
                  if (next.navigateOnly && next.href) {
                    player.setExpanded(false);
                    navigate(next.href);
                  } else {
                    player.playNextInQueue();
                  }
                }}
                className="flex-shrink-0 w-11 h-11 rounded-full bg-transparent text-ic-sky border-0 inline-flex items-center justify-center cursor-pointer transition-all duration-150 ease-out hover:bg-white/10 focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
              >
                <PlayIcon size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
